import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { 
  ArrowLeft,
  FileText, 
  Calendar, 
  Target,
  BarChart3,
  Clock,
  CheckCircle,
  AlertCircle,
  Download,
  Edit,
  Share2,
  Trash2,
  Eye,
  Users,
  Building,
  MapPin,
  DollarSign,
} from 'lucide-react';
import { Container } from '../styles/GlobalStyles';
import { theme } from '../styles/theme';
import { BentoGridItem } from '../components/BentoGrid';
import Particles from '../components/Particles';
import FinalReportSummary from '../components/FinalReportSummary';
import { FinalReport, Question, EvaluationCriteria, RefinedResponse } from '../types';
import { ProjectApi, ProjectData } from '../services/projectApi';
import { AnalysisAPI } from '../services/api';
import { useToastNotifications } from '../context/ToastContext';
import { transformRefinedQuestions } from '../utils/dataTransformers';

const ProjectDetailContainer = styled.div`
  min-height: calc(100vh - 72px);
  background: ${theme.colors.backgroundSecondary};
  padding: ${theme.spacing[8]} 0;
  margin: 0 30px;
  border: 1px solid ${theme.colors.border};
  border-radius: 20px;
  margin-top: 30px;
  position: relative;
  overflow: hidden;
  
  @media (max-width: ${theme.breakpoints.md}) {
    margin: 20px;
  }
  
  @media (max-width: ${theme.breakpoints.sm}) {
    margin: 0;
    border-radius: 0;
    border: none;
    margin-top: 0;
  }
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 1;
`;

const BackNavigation = styled.button`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[2]} 0;
  background: transparent;
  border: none;
  color: ${theme.colors.textSecondary};
  font-size: ${theme.typography.fontSize.sm};
  cursor: pointer;
  transition: all ${theme.transitions.base};
  margin-bottom: ${theme.spacing[6]};
  
  &:hover {
    color: ${theme.colors.primary[600]};
  }
`;

const ProjectHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${theme.spacing[6]};
  margin-bottom: ${theme.spacing[8]};
  
  @media (max-width: ${theme.breakpoints.lg}) {
    flex-direction: column;
    gap: ${theme.spacing[4]};
  }
`;

const HeaderContent = styled.div`
  flex: 1;
`;

const HeaderMeta = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[3]};
  flex-wrap: wrap;
`;

const StatusBadge = styled.div<{ $status: 'completed' | 'in_progress' | 'pending' | 'error' }>`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[2]} ${theme.spacing[4]};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  
  ${({ $status }) => {
    switch ($status) {
      case 'completed':
        return `
          background: ${theme.colors.success[100]};
          color: ${theme.colors.success[700]};
        `;
      case 'in_progress':
        return `
          background: ${theme.colors.warning[100]};
          color: ${theme.colors.warning[700]};
        `;
      case 'pending':
        return `
          background: ${theme.colors.info[100]};
          color: ${theme.colors.info[700]};
        `;
      case 'error':
        return `
          background: ${theme.colors.error[100]};
          color: ${theme.colors.error[700]};
        `;
    }
  }}
`;

const ProjectTitle = styled.h1`
  font-size: ${theme.typography.fontSize['4xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.textPrimary};
  margin-bottom: ${theme.spacing[3]};
  font-family: ${theme.typography.fontFamily.display};
  line-height: ${theme.typography.lineHeight.tight};
`;

const ProjectDescription = styled.p`
  color: ${theme.colors.textSecondary};
  font-size: ${theme.typography.fontSize.lg};
  line-height: ${theme.typography.lineHeight.relaxed};
  margin-bottom: ${theme.spacing[4]};
`;

const MetaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[6]};
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  color: ${theme.colors.textMuted};
  font-size: ${theme.typography.fontSize.sm};
`;

const HeaderActions = styled.div`
  display: flex;
  gap: ${theme.spacing[3]};
  flex-wrap: wrap;
`;

const ActionButton = styled.button<{ $variant?: 'primary' | 'secondary' | 'danger' }>`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${theme.transitions.base};
  border: 1px solid;
  
  ${({ $variant = 'secondary' }) => {
    switch ($variant) {
      case 'primary':
        return `
          background: linear-gradient(135deg, ${theme.colors.primary[500]}, ${theme.colors.accent[600]});
          color: white;
          border-color: transparent;
          
          &:hover {
            transform: translateY(-2px);
            box-shadow: ${theme.shadows.lg};
          }
        `;
      case 'danger':
        return `
          background: ${theme.colors.surface};
          color: ${theme.colors.error[600]};
          border-color: ${theme.colors.error[500]};
          
          &:hover {
            background: ${theme.colors.error[100]};
            border-color: ${theme.colors.error[600]};
          }
        `;
      default:
        return `
          background: ${theme.colors.surface};
          color: ${theme.colors.textSecondary};
          border-color: ${theme.colors.border};
          
          &:hover {
            background: ${theme.colors.primary[50]};
            color: ${theme.colors.primary[700]};
            border-color: ${theme.colors.primary[300]};
          }
        `;
    }
  }}
`;

const MainContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 350px;
  gap: ${theme.spacing[8]};
  
  @media (max-width: ${theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing[6]};
  }
`;

const ContentGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
`;

const ScoreOverview = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: ${theme.spacing[4]};
`;

const ScoreCard = styled.div`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing[5]};
  text-align: center;
  box-shadow: ${theme.shadows.sm};
  transition: all ${theme.transitions.base};
  
  &:hover {
    border-color: ${theme.colors.primary[300]};
    transform: translateY(-2px);
  }
`;

const ScoreValue = styled.div`
  font-size: ${theme.typography.fontSize['3xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.primary[600]};
  margin-bottom: ${theme.spacing[2]};
`;

const ScoreLabel = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textMuted};
  font-weight: ${theme.typography.fontWeight.medium};
`;

const TabNavigation = styled.div`
  display: flex;
  gap: ${theme.spacing[1]};
  margin-bottom: ${theme.spacing[6]};
  border-bottom: 1px solid ${theme.colors.border};
`;

const TabButton = styled.button<{ $active?: boolean }>`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  background: transparent;
  border: none;
  color: ${({ $active }) => $active ? theme.colors.primary[600] : theme.colors.textMuted};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${theme.transitions.base};
  border-bottom: 2px solid ${({ $active }) => $active ? theme.colors.primary[500] : 'transparent'};
  
  &:hover:not(:disabled) {
    color: ${theme.colors.primary[600]};
    background: ${theme.colors.primary[100]};
    border-radius: ${theme.borderRadius.base};
  }
`;

const TabContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
`;

const CriteriaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: ${theme.spacing[4]};
`;

const CriteriaCard = styled.div<{ $priorityLevel: string }>`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing[4]};
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    width: 4px;
    height: 100%;
    background: ${({ $priorityLevel }) => {
      switch ($priorityLevel) {
        case 'Pass/Fail': return theme.colors.error[500];
        case 'Must': return theme.colors.error[600];
        case 'Should': return theme.colors.warning[500];
        case 'Could': return theme.colors.success[500];
        case 'Cost Effectiveness': return theme.colors.info[500];
        default: return theme.colors.textMuted;
      }
    }};
  }
`;

const CriteriaHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${theme.spacing[3]};
  gap: ${theme.spacing[2]};
`;

const CriteriaName = styled.h4`
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.textPrimary};
  margin: 0;
  flex: 1;
`;

const CriteriaWeight = styled.div`
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.bold};
  padding: ${theme.spacing[1]} ${theme.spacing[2]};
  background: ${theme.colors.primary[100]};
  color: ${theme.colors.primary[700]};
  border-radius: ${theme.borderRadius.full};
`;

const CriteriaDescription = styled.p`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textSecondary};
  margin: 0;
  line-height: ${theme.typography.lineHeight.normal};
`;

const ResponseCard = styled.div`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing[5]};
  margin-bottom: ${theme.spacing[4]};
`;

const ResponseHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${theme.spacing[4]};
  gap: ${theme.spacing[3]};
`;

const QuestionText = styled.h4`
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.textPrimary};
  margin: 0;
  flex: 1;
  line-height: ${theme.typography.lineHeight.tight};
`;

const SectionBadge = styled.div`
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.medium};
  padding: ${theme.spacing[1]} ${theme.spacing[3]};
  background: ${theme.colors.accent[100]};
  color: ${theme.colors.accent[700]};
  border-radius: ${theme.borderRadius.full};
  white-space: nowrap;
`;

const ResponseText = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textSecondary};
  line-height: ${theme.typography.lineHeight.relaxed};
  white-space: pre-wrap;
`;

const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
`;

// Types for analysis data
interface AnalysisData {
  questions: Question[];
  evaluation_criteria: EvaluationCriteria[];
  overall_score: number;
  initial_score?: number;
  improvement_rate?: number;
  refined_responses: RefinedResponse[];
  initial_review_result?: any;
  final_report?: string;
  submission_justification?: string;
}

const ProjectDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [project, setProject] = useState<ProjectData | null>(null);
  const [analysisData, setAnalysisData] = useState<AnalysisData | null>(null);
  const [latestAnalysisThreadId, setLatestAnalysisThreadId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingError, setLoadingError] = useState<string | null>(null);
  const { showError } = useToastNotifications();

  // Fetch project data and analysis
  const fetchProjectData = async () => {
    if (!id) {
      setLoadingError('Project ID not provided');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setLoadingError(null);

      // Fetch project details
      const projectResponse = await ProjectApi.getProject(id);
      if (!projectResponse.success || !projectResponse.data) {
        throw new Error(projectResponse.error || 'Failed to fetch project');
      }

      setProject(projectResponse.data);

      // Fetch analysis data for this project
      const analysisResponse = await AnalysisAPI.listAnalysisSessions({ 
        project_id: id, 
        page_size: 100 
      });

      if (analysisResponse.success && analysisResponse.data?.sessions?.length > 0) {
        // Get the most recent completed analysis
        const completedAnalyses = analysisResponse.data.sessions.filter(
          (s: any) => s.status === 'completed'
        );

        if (completedAnalyses.length > 0) {
          const latestAnalysis = completedAnalyses[0];
          const results = latestAnalysis.results;
          
          // Store the threadId for detailed analysis navigation
          setLatestAnalysisThreadId(latestAnalysis.thread_id);

          // Extract scores - prioritize final score over initial score
          const initialScore = results?.initial_review_result?.score || 0;
          let finalScore = initialScore;
          let improvementRate = 0;

          // Try to extract final score from quality_check_result or final_report
          if (results?.quality_check_result?.score) {
            finalScore = results.quality_check_result.score;
          } else if (results?.final_report) {
            // Try to parse final score from final_report string
            const scoreMatch = results.final_report.match(/overall score[:\s]*(\d+)/i);
            if (scoreMatch) {
              finalScore = parseInt(scoreMatch[1]);
            }
          }

          // Calculate improvement rate
          if (finalScore && initialScore) {
            improvementRate = finalScore - initialScore;
          }

          // Transform analysis results into our expected format
          const analysisData: AnalysisData = {
            questions: results?.questions || [],
            evaluation_criteria: results?.evaluation_criteria || [],
            overall_score: finalScore,
            initial_score: initialScore,
            improvement_rate: improvementRate,
            refined_responses: transformRefinedQuestions(results?.refined_questions) || [],
            initial_review_result: results?.initial_review_result,
            final_report: results?.final_report,
            submission_justification: results?.improvement_analysis?.summary || 'Analysis completed successfully.'
          };

          setAnalysisData(analysisData);
        }
      }
    } catch (error) {
      console.error('Error fetching project data:', error);
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
      setLoadingError(errorMessage);
      showError('Error Loading Project', errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchProjectData();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBack = () => {
    navigate('/projects');
  };

  const handleEdit = () => {
    navigate(`/analysis/${id}`);
  };

  const handleDownload = () => {
    // Implement download functionality
    console.log('Download project report');
  };

  const handleShare = () => {
    // Implement share functionality
    console.log('Share project');
  };

  const handleDelete = () => {
    // Implement delete functionality
    if (window.confirm('Are you sure you want to delete this project?')) {
      console.log('Delete project');
      navigate('/projects');
    }
  };

  const handleViewAnalysisDetails = () => {
    if (latestAnalysisThreadId && id) {
      navigate(`/projects/${id}/analysis/${latestAnalysisThreadId}`);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle size={18} />;
      case 'in_progress':
        return <Clock size={18} />;
      case 'pending':
        return <AlertCircle size={18} />;
      case 'error':
        return <AlertCircle size={18} />;
      default:
        return <FileText size={18} />;
    }
  };

  const formatDate = (dateString: string | Date) => {
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Helper to get analysis status for display
  const getAnalysisStatus = (): 'completed' | 'in_progress' | 'pending' | 'error' => {
    if (analysisData) return 'completed';
    return 'pending';
  };

  // Dynamic tabs based on available data
  const getAvailableTabs = () => {
    const baseTabs = [{ key: 'overview', label: 'Overview' }];
    
    if (analysisData) {
      if (analysisData.evaluation_criteria.length > 0) {
        baseTabs.push({ key: 'criteria', label: 'Evaluation Criteria' });
      }
      if (analysisData.refined_responses.length > 0) {
        baseTabs.push({ key: 'responses', label: 'Responses' });
      }
    }
    
    baseTabs.push({ key: 'timeline', label: 'Timeline' });
    return baseTabs;
  };

  const tabs = getAvailableTabs();

  return (
    <ProjectDetailContainer>
      <Particles 
        particleCount={20}
        particleColor={theme.colors.primary[300]}
        speed={0.15}
        opacity={0.25}
      />
      <ContentWrapper>
        <Container>
          {/* Loading State */}
          {isLoading && (
            <div style={{ textAlign: 'center', padding: theme.spacing[12] }}>
              <p style={{ color: theme.colors.textMuted, fontSize: theme.typography.fontSize.lg }}>
                Loading project details...
              </p>
            </div>
          )}

          {/* Error State */}
          {loadingError && (
            <BentoGridItem
              title="Error Loading Project"
              description={loadingError}
              icon={<AlertCircle size={24} />}
            >
              <div style={{ textAlign: 'center', padding: theme.spacing[8] }}>
                <ActionButton onClick={() => navigate('/projects')}>
                  <ArrowLeft size={16} />
                  Back to Projects
                </ActionButton>
              </div>
            </BentoGridItem>
          )}

          {/* Project Content */}
          {!isLoading && !loadingError && project && (
            <>
              <BackNavigation onClick={handleBack}>
            <ArrowLeft size={16} />
            Back to Projects
          </BackNavigation>

          <ProjectHeader>
            <HeaderContent>
              {project && (
                <>
                  <HeaderMeta>
                    <StatusBadge $status={getAnalysisStatus()}>
                      {getStatusIcon(getAnalysisStatus())}
                      {getAnalysisStatus().replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())} Analysis
                    </StatusBadge>
                    <span style={{ color: theme.colors.textMuted, fontSize: theme.typography.fontSize.sm }}>
                      Project ID: {project.id}
                    </span>
                  </HeaderMeta>
                  
                  <ProjectTitle>{project.name}</ProjectTitle>
                  <ProjectDescription>{project.description || 'No description available'}</ProjectDescription>
                  
                  <MetaGrid>
                    <MetaItem>
                      <Building size={16} />
                      {project.industry}
                    </MetaItem>
                    <MetaItem>
                      <Target size={16} />
                      Priority: {project.priority.charAt(0).toUpperCase() + project.priority.slice(1)}
                    </MetaItem>
                    {project.estimated_value && (
                      <MetaItem>
                        <DollarSign size={16} />
                        {project.estimated_value}
                      </MetaItem>
                    )}
                    {project.deadline && (
                      <MetaItem>
                        <Calendar size={16} />
                        Due: {formatDate(project.deadline)}
                      </MetaItem>
                    )}
                    <MetaItem>
                      <Calendar size={16} />
                      Created: {formatDate(project.created_at)}
                    </MetaItem>
                    <MetaItem>
                      <Clock size={16} />
                      Updated: {formatDate(project.updated_at)}
                    </MetaItem>
                  </MetaGrid>
                </>
              )}
            </HeaderContent>

            <HeaderActions>
              {analysisData && latestAnalysisThreadId && (
                <ActionButton $variant="secondary" onClick={handleViewAnalysisDetails}>
                  <Eye size={16} />
                  View Details Analysis
                </ActionButton>
              )}
              <ActionButton $variant="primary" onClick={handleDownload}>
                <Download size={16} />
                Download
              </ActionButton>
              <ActionButton onClick={handleEdit}>
                <Edit size={16} />
                Edit
              </ActionButton>
              <ActionButton onClick={handleShare}>
                <Share2 size={16} />
                Share
              </ActionButton>
              <ActionButton $variant="danger" onClick={handleDelete}>
                <Trash2 size={16} />
                Delete
              </ActionButton>
            </HeaderActions>
          </ProjectHeader>

          <MainContent>
            <ContentGrid>
              <BentoGridItem
                title="Project Metrics"
                description="Key performance indicators and scores"
                icon={<BarChart3 size={20} />}
              >
                {analysisData ? (
                  <ScoreOverview>
                    <ScoreCard>
                      <ScoreValue>{analysisData.overall_score}%</ScoreValue>
                      <ScoreLabel>Overall Score</ScoreLabel>
                    </ScoreCard>
                    <ScoreCard>
                      <ScoreValue>{analysisData.evaluation_criteria.length}</ScoreValue>
                      <ScoreLabel>Criteria</ScoreLabel>
                    </ScoreCard>
                    <ScoreCard>
                      <ScoreValue>{analysisData.refined_responses.length}</ScoreValue>
                      <ScoreLabel>Responses</ScoreLabel>
                    </ScoreCard>
                    <ScoreCard>
                      <ScoreValue>
                        {analysisData.overall_score >= 90 ? 'A+' : 
                         analysisData.overall_score >= 80 ? 'A' :
                         analysisData.overall_score >= 70 ? 'B' :
                         analysisData.overall_score >= 60 ? 'C' : 'D'}
                      </ScoreValue>
                      <ScoreLabel>Grade</ScoreLabel>
                    </ScoreCard>
                  </ScoreOverview>
                ) : (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: theme.spacing[8],
                    color: theme.colors.textMuted,
                    fontStyle: 'italic'
                  }}>
                    No analysis data available yet. Run an analysis to see metrics.
                  </div>
                )}
              </BentoGridItem>

              <div>
                <TabNavigation>
                  {tabs.map((tab) => (
                    <TabButton
                      key={tab.key}
                      $active={activeTab === tab.key}
                      onClick={() => setActiveTab(tab.key)}
                    >
                      {tab.label}
                    </TabButton>
                  ))}
                </TabNavigation>

                <TabContent>
                  {activeTab === 'overview' && project && (
                    <BentoGridItem
                      title="Project Summary"
                      description="Overview of project details and status"
                      icon={<Eye size={20} />}
                    >
                      <div style={{ padding: theme.spacing[4] }}>
                        {analysisData?.submission_justification && (
                          <>
                            <h4 style={{ marginBottom: theme.spacing[3], color: theme.colors.textPrimary }}>
                              Analysis Summary
                            </h4>
                            <p style={{ 
                              color: theme.colors.textSecondary, 
                              lineHeight: theme.typography.lineHeight.relaxed,
                              marginBottom: theme.spacing[4]
                            }}>
                              {analysisData.submission_justification}
                            </p>
                          </>
                        )}
                        
                        <h4 style={{ marginBottom: theme.spacing[3], color: theme.colors.textPrimary }}>
                          Project Details
                        </h4>
                        <MetaGrid>
                          <MetaItem>
                            <Calendar size={16} />
                            Created: {formatDate(project.created_at)}
                          </MetaItem>
                          <MetaItem>
                            <Clock size={16} />
                            Last Updated: {formatDate(project.updated_at)}
                          </MetaItem>
                          {project.deadline && (
                            <MetaItem>
                              <Target size={16} />
                              Deadline: {formatDate(project.deadline)}
                            </MetaItem>
                          )}
                          <MetaItem>
                            <Building size={16} />
                            Industry: {project.industry}
                          </MetaItem>
                          <MetaItem>
                            <Target size={16} />
                            Priority: {project.priority.charAt(0).toUpperCase() + project.priority.slice(1)}
                          </MetaItem>
                          <MetaItem>
                            <FileText size={16} />
                            Status: {project.status.charAt(0).toUpperCase() + project.status.slice(1)}
                          </MetaItem>
                        </MetaGrid>
                      </div>
                    </BentoGridItem>
                  )}

                  {activeTab === 'criteria' && analysisData && (
                    <BentoGridItem
                      title="Evaluation Criteria"
                      description="Assessment criteria and requirements"
                      icon={<Target size={20} />}
                    >
                      <CriteriaGrid>
                        {analysisData.evaluation_criteria.map((criterion: any, index) => (
                          <CriteriaCard key={index} $priorityLevel={criterion.priority_level || 'Should'}>
                            <CriteriaHeader>
                              <CriteriaName>{criterion.name}</CriteriaName>
                              {criterion.weight && (
                                <CriteriaWeight>{criterion.weight}%</CriteriaWeight>
                              )}
                            </CriteriaHeader>
                            <CriteriaDescription>
                              {criterion.description || 'No description provided'}
                            </CriteriaDescription>
                          </CriteriaCard>
                        ))}
                      </CriteriaGrid>
                    </BentoGridItem>
                  )}

                  {activeTab === 'responses' && analysisData && (
                    <BentoGridItem
                      title="Refined Responses"
                      description="AI-optimised responses to tender questions"
                      icon={<FileText size={20} />}
                    >
                      {analysisData.refined_responses.length > 0 ? (
                        analysisData.refined_responses.map((response, index) => (
                          <ResponseCard key={index}>
                            <ResponseHeader>
                              <QuestionText>{response.question}</QuestionText>
                              <SectionBadge>Response {index + 1}</SectionBadge>
                            </ResponseHeader>
                            <ResponseText>{response.refined_response}</ResponseText>
                          </ResponseCard>
                        ))
                      ) : (
                        <div style={{ 
                          textAlign: 'center', 
                          padding: theme.spacing[8],
                          color: theme.colors.textMuted,
                          fontStyle: 'italic'
                        }}>
                          No refined responses available
                        </div>
                      )}
                    </BentoGridItem>
                  )}

                  {activeTab === 'timeline' && (
                    <BentoGridItem
                      title="Project Timeline"
                      description="Key milestones and progress tracking"
                      icon={<Clock size={20} />}
                    >
                      <div style={{ padding: theme.spacing[4], textAlign: 'center' }}>
                        <p style={{ color: theme.colors.textMuted }}>
                          Timeline visualization coming soon
                        </p>
                      </div>
                    </BentoGridItem>
                  )}
                </TabContent>
              </div>
            </ContentGrid>

            <Sidebar>
              {analysisData ? (
                <FinalReportSummary
                  finalReport={{
                    date: new Date().toISOString().split('T')[0],
                    overall_score: analysisData.overall_score,
                    max_score: 100,
                    evaluation_criteria: analysisData.evaluation_criteria.map((c: any) => ({
                      name: c.name,
                      weight: c.weight,
                      description: c.description || '',
                      priority_level: c.priority_level || 'Should'
                    })),
                    submission_justification: analysisData.submission_justification || '',
                    refined_responses: analysisData.refined_responses.map((response: any, index: number) => ({
                      question: response.question,
                      response: response.refined_response,
                      section_number: index + 1
                    })),
                    full_report_markdown: analysisData.final_report || ''
                  }}
                  onNavigateToSection={(section) => setActiveTab(section)}
                />
              ) : (
                <BentoGridItem
                  title="Analysis Summary"
                  description="Analysis results will appear here"
                  icon={<BarChart3 size={20} />}
                >
                  <div style={{ 
                    textAlign: 'center', 
                    padding: theme.spacing[6],
                    color: theme.colors.textMuted,
                    fontStyle: 'italic'
                  }}>
                    No analysis data available. Start an analysis to see detailed results.
                  </div>
                </BentoGridItem>
              )}
            </Sidebar>
          </MainContent>
            </>
          )}
        </Container>
      </ContentWrapper>
    </ProjectDetailContainer>
  );
};

export default ProjectDetailPage;